import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Plus, Trash2 } from 'lucide-react';
import { PERMISSION_GROUPS, MODULES, type Role } from '@tengesa/shared';
import { api } from '../../lib/api';
import { useSession } from '../../app/session';
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  ErrorNotice,
  Field,
  FormError,
  PageHeader,
  Panel,
  Select,
  Spinner,
  TextInput,
  fieldErrors,
  useToast,
} from '../../ui';

const CURRENT_PHASE = 1;

export function RolesPage() {
  const roles = useQuery({ queryKey: ['roles'], queryFn: () => api.get<Role[]>('/api/roles') });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const selected = roles.data?.find((r) => r.id === selectedId) ?? roles.data?.[0] ?? null;

  return (
    <>
      <PageHeader
        title="Roles & permissions"
        description="A role is a set of permissions. Give each person the role that matches their job."
        actions={
          <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
            New role
          </Button>
        }
      />
      {roles.isLoading && <Spinner />}
      {roles.error && <ErrorNotice error={roles.error} onRetry={() => roles.refetch()} />}
      {roles.data && selected && (
        <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
          <Panel className="self-start">
            <ul className="p-2" role="listbox" aria-label="Roles">
              {roles.data.map((r) => (
                <li key={r.id}>
                  <button
                    role="option"
                    aria-selected={r.id === selected.id}
                    onClick={() => setSelectedId(r.id)}
                    className={clsx(
                      'flex w-full items-center justify-between gap-2 rounded-[8px] px-3 py-2.5 text-left',
                      r.id === selected.id ? 'bg-ledger-50 text-ledger-800' : 'hover:bg-paper',
                    )}
                  >
                    <span>
                      <span className="block font-medium">{r.name}</span>
                      <span className="block text-sm text-muted">
                        {r.userCount} {r.userCount === 1 ? 'person' : 'people'}
                      </span>
                    </span>
                    {r.isSystem && <Badge>Built-in</Badge>}
                  </button>
                </li>
              ))}
            </ul>
          </Panel>
          <RoleEditor key={selected.id} role={selected} onDeleted={() => setSelectedId(null)} />
        </div>
      )}
      {creating && (
        <CreateRoleDialog
          onClose={() => setCreating(false)}
          onCreated={(r) => {
            setCreating(false);
            setSelectedId(r.id);
          }}
        />
      )}
    </>
  );
}

function RoleEditor({ role, onDeleted }: { role: Role; onDeleted: () => void }) {
  const { me, hasModule } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const mine = useMemo(() => new Set<string>(me.user.permissions), [me.user.permissions]);
  const [name, setName] = useState(role.name);
  const [description, setDescription] = useState(role.description);
  const [perms, setPerms] = useState<Set<string>>(new Set(role.permissions));
  const [confirmDelete, setConfirmDelete] = useState(false);
  useEffect(() => {
    setPerms(new Set(role.permissions));
  }, [role.permissions]);

  const isOwner = role.key === 'owner';
  const isMine = role.id === me.user.roleId;
  const locked = isOwner || isMine;
  const dirty =
    name !== role.name || description !== role.description || perms.size !== role.permissions.length || role.permissions.some((p) => !perms.has(p));

  const save = useMutation({
    mutationFn: () => api.put<Role>(`/api/roles/${role.id}`, { name, description, permissions: [...perms] }),
    onSuccess: () => {
      toast(`${name} saved. Changes apply to everyone with this role straight away.`);
      void qc.invalidateQueries({ queryKey: ['roles'] });
    },
  });
  const del = useMutation({
    mutationFn: () => api.del(`/api/roles/${role.id}`),
    onSuccess: () => {
      toast(`${role.name} deleted`);
      void qc.invalidateQueries({ queryKey: ['roles'] });
      onDeleted();
    },
  });
  const f = fieldErrors(save.error);

  const toggle = (key: string, on: boolean) =>
    setPerms((s) => {
      const n = new Set(s);
      if (on) n.add(key);
      else n.delete(key);
      return n;
    });

  return (
    <Panel>
      <div className="border-b border-line p-5">
        {locked ? (
          <>
            <h2 className="text-xl font-semibold">{role.name}</h2>
            <p className="mt-1 text-muted">{role.description}</p>
            <p className="mt-3 rounded-[8px] bg-gold-soft px-3 py-2 text-sm text-gold-ink">
              {isOwner
                ? 'The Owner role always has every permission, including those added in future updates. It cannot be edited.'
                : "This is your own role. Someone else must change it, so nobody can raise their own access."}
            </p>
          </>
        ) : (
          <div className="grid gap-4 sm:grid-cols-[1fr_1.5fr]">
            <Field label="Role name" htmlFor="role-name" error={f.name} hint={role.isSystem ? 'Built-in roles keep their name.' : undefined}>
              <TextInput id="role-name" value={name} onChange={(e) => setName(e.target.value)} disabled={role.isSystem} invalid={!!f.name} />
            </Field>
            <Field label="What this role is for" htmlFor="role-desc" error={f.description}>
              <TextInput id="role-desc" value={description} onChange={(e) => setDescription(e.target.value)} />
            </Field>
          </div>
        )}
      </div>
      <div className="p-5">
        <FormError error={save.error && !Object.keys(f).length ? save.error : null} />
        <div className="space-y-6">
          {PERMISSION_GROUPS.map((g) => {
            const items = g.permissions.filter((p) => !('module' in p) || hasModule(p.module as never));
            if (!items.length) return null;
            const upcoming = g.phase > CURRENT_PHASE;
            return (
              <fieldset key={g.key}>
                <legend className="mb-2 flex items-center gap-2">
                  <span className="font-semibold">{g.label}</span>
                  {upcoming && <Badge>Screens arrive in phase {g.phase}</Badge>}
                </legend>
                <div className="grid gap-x-6 gap-y-2.5 sm:grid-cols-2">
                  {items.map((p) => {
                    const notMine = !mine.has(p.key);
                    const mod = 'module' in p ? MODULES[p.module as keyof typeof MODULES] : null;
                    return (
                      <Checkbox
                        key={p.key}
                        checked={perms.has(p.key)}
                        onChange={(v) => toggle(p.key, v)}
                        disabled={locked || notMine}
                        label={
                          <>
                            {p.label}
                            {'sensitive' in p && p.sensitive && (
                              <span className="ml-1.5 align-middle">
                                <Badge tone="warn">Sensitive</Badge>
                              </span>
                            )}
                          </>
                        }
                        description={notMine && !locked ? "You don't have this permission, so you can't change it" : mod ? `${mod.label} feature` : undefined}
                      />
                    );
                  })}
                </div>
              </fieldset>
            );
          })}
        </div>
      </div>
      {!locked && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-5 py-4">
          {!role.isSystem ? (
            <Button variant="ghost" icon={<Trash2 className="size-4" />} className="text-danger" onClick={() => setConfirmDelete(true)}>
              Delete role
            </Button>
          ) : (
            <span className="text-sm text-muted">Built-in roles can be adjusted but not deleted.</span>
          )}
          <div className="flex gap-2">
            <Button
              disabled={!dirty}
              onClick={() => {
                setName(role.name);
                setDescription(role.description);
                setPerms(new Set(role.permissions));
              }}
            >
              Undo changes
            </Button>
            <Button variant="primary" disabled={!dirty} busy={save.isPending} onClick={() => save.mutate()}>
              Save role
            </Button>
          </div>
        </div>
      )}
      {confirmDelete && (
        <Dialog
          open
          onClose={() => setConfirmDelete(false)}
          title={`Delete ${role.name}?`}
          description={role.userCount ? `${role.userCount} active staff still have this role. Move them to another role first.` : 'Nobody has this role. This cannot be undone.'}
          footer={
            <>
              <Button onClick={() => setConfirmDelete(false)}>Keep role</Button>
              <Button variant="danger" busy={del.isPending} onClick={() => del.mutate()}>
                Delete role
              </Button>
            </>
          }
        >
          {del.error ? <ErrorNotice error={del.error} /> : <p className="text-muted">The change is recorded in the audit log.</p>}
        </Dialog>
      )}
    </Panel>
  );
}

function CreateRoleDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (r: Role) => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const roles = qc.getQueryData<Role[]>(['roles']) ?? [];
  const { me } = useSession();
  const mine = new Set<string>(me.user.permissions);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [copyFrom, setCopyFrom] = useState('');
  const m = useMutation({
    mutationFn: () => {
      const base = roles.find((r) => r.id === copyFrom);
      return api.post<Role>('/api/roles', { name, description, permissions: (base?.permissions ?? []).filter((p) => mine.has(p)) });
    },
    onSuccess: (r) => {
      toast(`${r.name} created. Now tick the permissions it needs.`);
      void qc.invalidateQueries({ queryKey: ['roles'] });
      onCreated(r);
    },
  });
  const f = fieldErrors(m.error);
  return (
    <Dialog
      open
      onClose={onClose}
      title="New role"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={m.isPending} onClick={() => m.mutate()}>
            Create role
          </Button>
        </>
      }
    >
      <FormError error={m.error && !Object.keys(f).length ? m.error : null} />
      <div className="space-y-4">
        <Field label="Role name" htmlFor="new-role" error={f.name} hint="For example: Supervisor, Butcher, Delivery Driver">
          <TextInput id="new-role" value={name} onChange={(e) => setName(e.target.value)} autoFocus invalid={!!f.name} />
        </Field>
        <Field label="What this role is for" htmlFor="new-role-desc">
          <TextInput id="new-role-desc" value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <Field label="Start from" htmlFor="copy-from" hint="Copies the permissions of an existing role. You can change them afterwards.">
          <Select id="copy-from" value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)}>
            <option value="">No permissions</option>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </Select>
        </Field>
      </div>
    </Dialog>
  );
}
